import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  TaxHistoryData,
  TaxProjectionData,
  TaxSuggestionData,
} from "./EncargosAccordion";
import pencil from "../../Assets/pencil.svg";
import deleteIcon from "../../Assets/deleteIcon.svg";
import corret from "../../Assets/correct.png";
import faradayApi from "../../Services/faradayApi";
import {
  maskBrazilianDecimalInput,
  parseBrazilianNumber,
} from "../../utils/utilityFunctions";

const HISTORICAL_SUGGESTION_NOTICE =
  "A sugestão de aporte está disponível apenas para o mês anterior e o vigente.";

const fmtBRL = (v: number) =>
  v?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDate = (date: string) =>
  new Date(date).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export function TableProjecao({
  projection,
  projectionByUnit,
  finalized,
  balances,
  suggestion,
  isSuggestionAvailable = true,
}: {
  projection: TaxProjectionData[];
  projectionByUnit: TaxProjectionData[];
  finalized: TaxHistoryData[];
  balances: {
    agentName: string;
    companyBalance: number | null;
    lastBalanceUpdate: string | null;
  }[];
  suggestion: TaxSuggestionData[];
  isSuggestionAvailable?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(-1);
  const [newBalances, setNewBalances] = useState<number[]>([]);
  const [balancesData, setBalancesData] = useState(balances);
  const [expandedAgentIdx, setExpandedAgentIdx] = useState(-1);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);

  const handleEditBalance = async (
    idx: number,
    agentName: string,
    value: number
  ) => {
    if (value === undefined || Number.isNaN(value)) return;

    setSavingIdx(idx);
    try {
      const response = await faradayApi.patch(
        `companies/balance/${agentName}`,
        { balance: value }
      );

      setBalancesData((prev) =>
        prev.map((item) =>
          item.agentName === agentName
            ? {
                ...item,
                companyBalance: response.data.balance,
                lastBalanceUpdate: response.data.last_balance_update,
              }
            : item
        )
      );
      setIsEditing(-1);
      setNewBalances([]);
    } catch (error) {
      window.alert(
        "Erro ao salvar o saldo do agente, por favor, tente novamente."
      );
    } finally {
      setSavingIdx(null);
    }
  };

  const findFinalizedTaxes = (item: TaxProjectionData) =>
    finalized.find(
      (final) =>
        item.agentName === final.agentName &&
        item.month === final.month &&
        item.year === final.year
    );

  const findAgentBalance = (agentName: string) =>
    balancesData.find(
      (balance) =>
        agentName === balance.agentName && balance.companyBalance !== undefined
    );

  const findAgentUnits = (agentName: string) =>
    projectionByUnit.filter((unit) => unit.agentName === agentName);

  const findAgentSuggestion = (agentName: string) =>
    suggestion.find((item) => item.agentName === agentName);

  return (
    <>
      <div className="d-md-none" style={{ maxHeight: 320, overflowY: "auto" }}>
        {Array.isArray(projection)
          ? projection.map((item, idx) => {
              const finalizedTaxes = findFinalizedTaxes(item);
              const agentBalance = findAgentBalance(item.agentName);
              const agentUnits = findAgentUnits(item.agentName);
              const agentSuggestion = findAgentSuggestion(item.agentName);
              const isExpanded = expandedAgentIdx === idx;

              return (
                <React.Fragment key={idx}>
                  <div className="card mb-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between text-center align-items-center">
                        <strong>Agente</strong>
                        <span className="d-flex align-items-center">
                          {item.agentName}
                          {agentUnits.length > 0 && (
                            <button
                              className="bg-transparent border-0 p-0 ml-2"
                              onClick={() =>
                                setExpandedAgentIdx((prev) =>
                                  prev !== idx ? idx : -1
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                style={{
                                  transform: isExpanded
                                    ? "rotate(180deg)"
                                    : "none",
                                  transition: "transform 0.2s",
                                }}
                              />
                            </button>
                          )}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between text-center">
                        <strong>Tipo</strong>
                        <span>{item.type}</span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Energia de reserva</strong>
                        <span>{fmtBRL(item.energyReserveTax)}</span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Garantia financeira</strong>
                        <span className="text-truncate">
                          {fmtBRL(item.financialGuaranteeTax)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Reserva de capacidade</strong>
                        <span>{fmtBRL(item.capacityReserveTax)}</span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Cota nuclear</strong>
                        <span>{fmtBRL(item.quotaNuclearTax)}</span>
                      </div>

                      <hr className="my-2" />

                      <div className="d-flex justify-content-between text-center">
                        <span>Total de encargos</span>
                        <strong>
                          {fmtBRL(item.totalChargesExcludingMcpCredit)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {isExpanded &&
                    agentUnits.map((unit, unitIdx) => (
                      <div className="card mb-2 ml-3" key={`unit-${unitIdx}`}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between text-center">
                            <strong>Unidade</strong>
                            <span>{unit.name}</span>
                          </div>

                          <div className="d-flex justify-content-between text-center">
                            <strong>Tipo</strong>
                            <span>{unit.type}</span>
                          </div>
                          <div className="d-flex justify-content-between text-center">
                            <strong>Energia de reserva</strong>
                            <span>{fmtBRL(unit.energyReserveTax)}</span>
                          </div>
                          <div className="d-flex justify-content-between text-center">
                            <strong>Garantia financeira</strong>
                            <span className="text-truncate">
                              {fmtBRL(unit.financialGuaranteeTax)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between text-center">
                            <strong>Reserva de capacidade</strong>
                            <span>{fmtBRL(unit.capacityReserveTax)}</span>
                          </div>
                          <div className="d-flex justify-content-between text-center">
                            <strong>Cota nuclear</strong>
                            <span>{fmtBRL(unit.quotaNuclearTax)}</span>
                          </div>

                          <hr className="my-2" />

                          <div className="d-flex justify-content-between text-center">
                            <span>Total de encargos</span>
                            <strong>
                              {fmtBRL(unit.totalChargesExcludingMcpCredit)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    ))}

                  <div className="card mb-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between text-center">
                        <strong>Agente</strong>
                        <span>{item.agentName}</span>
                      </div>

                      <div className="d-flex justify-content-between text-center">
                        <strong>Tipo</strong>
                        <span>Consolidado</span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Energia de reserva</strong>
                        <span>
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.energyReserveTax)
                            : "-"}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Garantia financeira</strong>
                        <span className="text-truncate">
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.financialGuaranteeTax)
                            : "-"}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Reserva de capacidade</strong>
                        <span>
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.capacityReserveTax)
                            : "-"}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-center">
                        <strong>Cota nuclear</strong>
                        <span>
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.quotaNuclearTax)
                            : "-"}
                        </span>
                      </div>

                      <hr className="my-2" />

                      <div className="d-flex justify-content-between text-center">
                        <span>Total de encargos</span>
                        <strong>
                          {finalizedTaxes
                            ? fmtBRL(
                                finalizedTaxes.totalChargesExcludingMcpCredit
                              )
                            : "-"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {finalizedTaxes && (
                    <div className="card mb-2">
                      <div className="card-body">
                        <div className="d-flex justify-content-between text-center">
                          <strong>Agente</strong>
                          <span>{item.agentName}</span>
                        </div>

                        <div className="d-flex justify-content-between text-center">
                          <strong>Tipo</strong>
                          <span>Diferença</span>
                        </div>
                        <div className="d-flex justify-content-between text-center">
                          <strong>Energia de reserva</strong>
                          <span>
                            {fmtBRL(
                              item.energyReserveTax -
                                finalizedTaxes.energyReserveTax
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between text-center">
                          <strong>Garantia financeira</strong>
                          <span className="text-truncate">
                            {fmtBRL(
                              item.financialGuaranteeTax -
                                finalizedTaxes.financialGuaranteeTax
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between text-center">
                          <strong>Reserva de capacidade</strong>
                          <span>
                            {fmtBRL(
                              item.capacityReserveTax -
                                finalizedTaxes.capacityReserveTax
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between text-center">
                          <strong>Cota nuclear</strong>
                          <span>
                            {fmtBRL(
                              item.quotaNuclearTax -
                                finalizedTaxes.quotaNuclearTax
                            )}
                          </span>
                        </div>

                        <hr className="my-2" />

                        <div className="d-flex justify-content-between text-center">
                          <span>Total de encargos</span>
                          <strong>
                            {fmtBRL(
                              item.totalChargesExcludingMcpCredit -
                                finalizedTaxes.totalChargesExcludingMcpCredit
                            )}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}

                  {agentBalance && (
                    <div
                      className="card mb-2"
                      style={{ backgroundColor: "#e8f4fd" }}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center text-center">
                          <strong>Saldo {agentBalance.agentName}</strong>
                          {isEditing === idx ? (
                            <input
                              type="text"
                              className="w-50"
                              autoFocus
                              defaultValue={
                                agentBalance.companyBalance
                                  ? `${agentBalance.companyBalance}`.replace(
                                      ".",
                                      ","
                                    )
                                  : 0
                              }
                              onChange={(e) => {
                                const masked = maskBrazilianDecimalInput(
                                  e.target.value
                                );
                                e.target.value = masked;
                                setNewBalances((prev) => {
                                  const updated = [...prev];
                                  updated[idx] = masked
                                    ? parseBrazilianNumber(masked)
                                    : 0;
                                  return updated;
                                });
                              }}
                              placeholder="Insira novo valor"
                            />
                          ) : agentBalance.companyBalance == null ? (
                            <button
                              className="bg-transparent border-0 p-0"
                              style={{ color: "#0072ce", fontWeight: "bold" }}
                              disabled={savingIdx !== null}
                              onClick={() => setIsEditing(idx)}
                            >
                              Atualize
                            </button>
                          ) : (
                            <span
                              style={{ color: "#28a745", fontWeight: "bold" }}
                            >
                              {fmtBRL(agentBalance.companyBalance)}
                            </span>
                          )}
                          <span>
                            <button
                              className="bg-transparent border-0 mr-2"
                              disabled={savingIdx !== null}
                              style={
                                savingIdx !== null
                                  ? { opacity: 0.5 }
                                  : undefined
                              }
                              onClick={() => {
                                setIsEditing((prev) =>
                                  prev !== idx ? idx : -1
                                );
                              }}
                            >
                              {isEditing === idx ? (
                                <img src={deleteIcon} alt="delete" width={16} />
                              ) : (
                                <img src={pencil} alt="edit" width={16} />
                              )}
                            </button>
                            {isEditing === idx && (
                              <button
                                className="bg-transparent border-0"
                                disabled={savingIdx === idx}
                                onClick={() => {
                                  handleEditBalance(
                                    idx,
                                    agentBalance.agentName,
                                    newBalances[idx]
                                  );
                                }}
                              >
                                {savingIdx === idx ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <img src={corret} alt="save" width={18} />
                                )}
                              </button>
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between text-center">
                          <strong>Última atualização</strong>
                          <span>
                            {agentBalance.lastBalanceUpdate
                              ? formatDate(agentBalance.lastBalanceUpdate)
                              : "Nunca foi atualizado"}
                          </span>
                        </div>

                        <hr className="my-2" />

                        <div className="d-flex justify-content-between text-center">
                          <strong>Sugestão de aporte</strong>
                          <span
                            style={{ color: "#28a745", fontWeight: "bold" }}
                          >
                            {!isSuggestionAvailable
                              ? HISTORICAL_SUGGESTION_NOTICE
                              : agentSuggestion
                              ? fmtBRL(agentSuggestion.amount)
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          : null}
      </div>

      <div className="d-none d-md-block" style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "16px",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #0072ce",
              }}
            >
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Agente
              </th>

              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Tipo
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Energia de reserva
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Garantia financeira
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Reserva de capacidade
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Cota nuclear
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Total de encargos
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(projection)
              ? projection.map((item, idx) => {
                  const finalizedTaxes = findFinalizedTaxes(item);
                  const agentBalance = findAgentBalance(item.agentName);
                  const agentUnits = findAgentUnits(item.agentName);
                  const agentSuggestion = findAgentSuggestion(item.agentName);
                  const isExpanded = expandedAgentIdx === idx;
                  return (
                    <React.Fragment
                      key={`${item.agentName}-${item.year}-${item.month}`}
                    >
                      <tr
                        key={idx}
                        style={{
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor:
                            idx % 2 === 0 ? "#ffffff" : "#f9f9f9",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                          }}
                        >
                          {item.agentName}
                          {agentUnits.length > 0 && (
                            <button
                              className="bg-transparent border-0 p-0 ml-2"
                              onClick={() =>
                                setExpandedAgentIdx((prev) =>
                                  prev !== idx ? idx : -1
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                style={{
                                  transform: isExpanded
                                    ? "rotate(180deg)"
                                    : "none",
                                  transition: "transform 0.2s",
                                }}
                              />
                            </button>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                          }}
                        >
                          {item.type}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {fmtBRL(item.energyReserveTax)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {fmtBRL(item.financialGuaranteeTax)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {fmtBRL(item.capacityReserveTax)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {fmtBRL(item.quotaNuclearTax)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {fmtBRL(item.totalChargesExcludingMcpCredit)}
                        </td>
                      </tr>
                      {isExpanded &&
                        agentUnits.map((unit, unitIdx) => (
                          <tr
                            key={`unit-${unitIdx}`}
                            style={{
                              borderBottom: "1px solid #e0e0e0",
                              backgroundColor:
                                idx % 2 === 0 ? "#ffffff" : "#f9f9f9",
                            }}
                          >
                            <td
                              style={{
                                padding: "12px 12px 12px 32px",
                                fontSize: "13px",
                              }}
                            >
                              {unit.name}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                fontSize: "13px",
                              }}
                            >
                              {unit.type}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {fmtBRL(unit.energyReserveTax)}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {fmtBRL(unit.financialGuaranteeTax)}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {fmtBRL(unit.capacityReserveTax)}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {fmtBRL(unit.quotaNuclearTax)}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {fmtBRL(unit.totalChargesExcludingMcpCredit)}
                            </td>
                          </tr>
                        ))}
                      <tr
                        style={{
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor:
                            idx % 2 === 0 ? "#ffffff" : "#f9f9f9",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                          }}
                        >
                          {item.agentName}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                          }}
                        >
                          Consolidado
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.energyReserveTax)
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.financialGuaranteeTax)
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.capacityReserveTax)
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {finalizedTaxes
                            ? fmtBRL(finalizedTaxes.quotaNuclearTax)
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            fontSize: "13px",
                          }}
                        >
                          {finalizedTaxes
                            ? fmtBRL(
                                finalizedTaxes.totalChargesExcludingMcpCredit
                              )
                            : "-"}
                        </td>
                      </tr>
                      {finalizedTaxes && (
                        <>
                          <tr
                            style={{
                              borderBottom: "2px solid #0072ce",
                              backgroundColor:
                                idx % 2 === 0 ? "#ffffff" : "#f9f9f9",
                            }}
                          >
                            <td
                              style={{
                                padding: "12px",
                                fontSize: "13px",
                              }}
                            >
                              {item.agentName}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                fontSize: "13px",
                              }}
                            >
                              Diferença
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {finalizedTaxes
                                ? fmtBRL(
                                    item.energyReserveTax -
                                      finalizedTaxes.energyReserveTax
                                  )
                                : "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {finalizedTaxes
                                ? fmtBRL(
                                    item.financialGuaranteeTax -
                                      finalizedTaxes.financialGuaranteeTax
                                  )
                                : "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {finalizedTaxes
                                ? fmtBRL(
                                    item.capacityReserveTax -
                                      finalizedTaxes.capacityReserveTax
                                  )
                                : "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {finalizedTaxes
                                ? fmtBRL(
                                    item.quotaNuclearTax -
                                      finalizedTaxes.quotaNuclearTax
                                  )
                                : "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                fontSize: "13px",
                              }}
                            >
                              {finalizedTaxes
                                ? fmtBRL(
                                    item.totalChargesExcludingMcpCredit -
                                      finalizedTaxes.totalChargesExcludingMcpCredit
                                  )
                                : "-"}
                            </td>
                          </tr>
                        </>
                      )}
                      {agentBalance && (
                        <tr
                          style={{
                            borderTop: "2px solid #0072ce",
                            backgroundColor: "#e8f4fd",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            Saldo {agentBalance.agentName}:
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              width: "130px",
                              textAlign: "center",
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "#28a745",
                            }}
                          >
                            {isEditing === idx ? (
                              <input
                                type="text"
                                className="w-75"
                                autoFocus
                                defaultValue={
                                  agentBalance.companyBalance
                                    ? `${agentBalance.companyBalance}`.replace(
                                        ".",
                                        ","
                                      )
                                    : 0
                                }
                                onChange={(e) => {
                                  const masked = maskBrazilianDecimalInput(
                                    e.target.value
                                  );
                                  e.target.value = masked;
                                  setNewBalances((prev) => {
                                    const updated = [...prev];
                                    updated[idx] = masked
                                      ? parseBrazilianNumber(masked)
                                      : 0;
                                    return updated;
                                  });
                                }}
                                placeholder="Insira novo valor"
                              />
                            ) : agentBalance.companyBalance == null ? (
                              <button
                                className="bg-transparent border-0 p-0"
                                style={{ color: "#0072ce", fontWeight: "bold" }}
                                disabled={savingIdx !== null}
                                onClick={() => setIsEditing(idx)}
                              >
                                Atualize
                              </button>
                            ) : (
                              fmtBRL(agentBalance.companyBalance)
                            )}
                          </td>
                          <td
                            style={{
                              textAlign: "left",
                            }}
                          >
                            <button
                              className="bg-transparent border-0 mr-2"
                              disabled={savingIdx !== null}
                              style={
                                savingIdx !== null
                                  ? { opacity: 0.5 }
                                  : undefined
                              }
                              onClick={() => {
                                setIsEditing((prev) => {
                                  if (prev !== idx) {
                                    return idx;
                                  } else {
                                    return -1;
                                  }
                                });
                              }}
                            >
                              {isEditing === idx ? (
                                <img src={deleteIcon} alt="delete" width={16} />
                              ) : (
                                <img src={pencil} alt="edit" width={16} />
                              )}
                            </button>
                            {isEditing === idx && (
                              <button
                                className="bg-transparent border-0"
                                disabled={savingIdx === idx}
                                onClick={() => {
                                  handleEditBalance(
                                    idx,
                                    agentBalance.agentName,
                                    newBalances[idx]
                                  );
                                }}
                              >
                                {savingIdx === idx ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <img src={corret} alt="save" width={18} />
                                )}
                              </button>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontSize: "14px",
                              fontWeight: "bold",
                              textAlign: "right",
                            }}
                          >
                            Última atualização:
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              fontSize: "14px",
                            }}
                          >
                            {agentBalance.lastBalanceUpdate
                              ? formatDate(agentBalance.lastBalanceUpdate)
                              : "Nunca foi atualizado"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontSize: "14px",
                              fontWeight: "bold",
                              textAlign: "right",
                            }}
                          >
                            Sugestão de aporte:
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "#28a745",
                            }}
                          >
                            {!isSuggestionAvailable
                              ? HISTORICAL_SUGGESTION_NOTICE
                              : agentSuggestion
                              ? fmtBRL(agentSuggestion.amount)
                              : "-"}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>
    </>
  );
}
